/**
 * Partner first-class entity fixture (ADR #020 — 2026-04-21).
 *
 * Hem sites fixture (partnerId ilişkisi) hem customer edit dialog
 * (partner picker options) hem de /partners Partners sayfası (CRUD)
 * tarafından okunur.
 *
 * Eski `MockPartner` yalnızca `{id, name}` idi. Yeni `Partner` artık
 * birinci sınıf entity: contact info, location, createdAt, isActive,
 * hasLogin (login yapabilirse `password` alanıyla birlikte).
 */

export interface Partner {
  id: string
  name: string
  contactEmail: string
  contactPhone: string | null
  country: string
  city: string
  createdAt: string
  isActive: boolean
  hasLogin: boolean
  /** Plaintext mock password; real backend hash'leyecek. */
  password: string | null
}

/** Geriye uyumluluk için korunan minimal shape. */
export interface MockPartner {
  id: string
  name: string
}

const nowIso = "2026-04-21T08:00:00Z"
const earlyIso = "2026-01-12T09:00:00Z"
const marchIso = "2026-03-04T10:30:00Z"

const seedPartners: Partner[] = [
  {
    id: "partner-north",
    name: "Kuzey Enerji Çözümleri",
    contactEmail: "info@kuzeyenerji.com.tr",
    contactPhone: "+90 362 555 0112",
    country: "Türkiye",
    city: "Samsun",
    createdAt: earlyIso,
    isActive: true,
    hasLogin: true,
    password: "partner123",
  },
  {
    id: "partner-aegean",
    name: "Ege Solar Partners",
    contactEmail: "contact@egesolar.com",
    contactPhone: "+90 232 555 0187",
    country: "Türkiye",
    city: "İzmir",
    createdAt: earlyIso,
    isActive: true,
    hasLogin: true,
    password: "partner123",
  },
  {
    id: "partner-south",
    name: "Akdeniz Grid Operations",
    contactEmail: "ops@akdenizgrid.com.tr",
    contactPhone: "+90 242 555 0143",
    country: "Türkiye",
    city: "Antalya",
    createdAt: marchIso,
    isActive: true,
    hasLogin: true,
    password: "partner123",
  },
  {
    id: "partner-central",
    name: "İç Anadolu EMS",
    contactEmail: "destek@icanadoluems.com.tr",
    contactPhone: "+90 312 555 0099",
    country: "Türkiye",
    city: "Ankara",
    createdAt: marchIso,
    isActive: true,
    hasLogin: false,
    password: null,
  },
  {
    id: "partner-marmara",
    name: "Marmara Enerji Sistemleri",
    contactEmail: "info@marmaraenerji.com",
    contactPhone: "+90 216 555 0421",
    country: "Türkiye",
    city: "İstanbul",
    createdAt: marchIso,
    isActive: true,
    hasLogin: true,
    password: "partner123",
  },
  {
    id: "partner-east",
    name: "Doğu Şebeke Teknolojileri",
    contactEmail: "contact@dogusebeke.com.tr",
    contactPhone: "+90 442 555 0368",
    country: "Türkiye",
    city: "Erzurum",
    createdAt: nowIso,
    isActive: false,
    hasLogin: false,
    password: null,
  },
  {
    id: "partner-blacksea",
    name: "Karadeniz Smart Grid",
    contactEmail: "info@ksgrid.com.tr",
    contactPhone: "+90 462 555 0221",
    country: "Türkiye",
    city: "Trabzon",
    createdAt: nowIso,
    isActive: true,
    hasLogin: true,
    password: "partner123",
  },
  {
    id: "partner-thrace",
    name: "Trakya Enerji Dağıtım",
    contactEmail: "destek@trakyaenerji.com",
    contactPhone: null,
    country: "Türkiye",
    city: "Edirne",
    createdAt: nowIso,
    isActive: true,
    hasLogin: false,
    password: null,
  },
]

/** In-memory mutable store (mock CRUD state). */
const partnerStore: Partner[] = seedPartners.map((p) => ({ ...p }))

/**
 * Static snapshot at module init for legacy consumers (sites fixture seed).
 * Post-init CRUD mutations NOT reflected here — use `getAllPartners()` for live data.
 */
export const MOCK_PARTNERS: readonly MockPartner[] = partnerStore.map((p) => ({
  id: p.id,
  name: p.name,
}))

export function getAllPartners(): Partner[] {
  return partnerStore.map((p) => ({ ...p }))
}

export function getMockPartner(id: string | null | undefined): MockPartner | null {
  if (!id) {
    return null
  }
  const found = partnerStore.find((p) => p.id === id)
  return found ? { id: found.id, name: found.name } : null
}

export function getPartnerById(id: string): Partner | null {
  const found = partnerStore.find((p) => p.id === id)
  return found ? { ...found } : null
}

// ADR #034 — CRUD mutator'ları (createMockPartner/updateMockPartner/
// deleteMockPartner/togglePartnerActive/findPartnerByEmail) kaldırıldı: tek
// tüketicileri emekli edilen SuperAdmin/partners handler'ıydı. Bu dosya artık
// yalnız SEED kaynağıdır — runtime mutasyon org store'da (organizations.ts).
